import React, { useState, useEffect, useRef, useCallback } from 'react';
import { analyticalMethodsAPI, equipmentAPI, cleaningConfigAPI } from '../services/api';
import useApprovalRights from '../hooks/useApprovalRights';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';
import { toast } from 'react-toastify';
import { FaSave, FaCheck, FaFileUpload, FaTimes, FaEye, FaTrash, FaInfoCircle, FaPaperclip, FaLayerGroup, FaFlask, FaExclamationTriangle, FaRulerCombined } from 'react-icons/fa';
import { StatCard } from '../components/UnifiedUIComponents';
import StatusBadge from '../components/StatusBadge';
import { ActionsMenu } from '../components/ActionsMenu';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');


const APPROVAL_DECISIONS = [
  { value: 'approve', label: 'Approve' },
  { value: 'return', label: 'Return to Draft' },
  { value: 'reject', label: 'Reject' },
];

const CONC_UNITS = ['µg/mL', 'µg/cm²', 'ppb', 'ppm', 'ng/mL', 'ng/cm²'];

const emptyRow = (surface_material) => ({
  surface_material,
  swab_chem_pct: '',
  rinse_chem_pct: '',
  // Detergent carryover is a different analyte from the API residue above —
  // its own recovery study, only meaningful when this line actually uses a
  // detergent (Cleaning Config), hence shown as a conditional column group.
  swab_detergent_pct: '',
  rinse_detergent_pct: '',
  swab_micro_pct: '',
  rinse_micro_pct: '',
  lod_value: '',
  lod_unit: 'µg/mL',
  loq_value: '',
  loq_unit: 'µg/mL',
  protocol_files: [],
  report_files: [],
  study_files: [],
  remarks: '',
});

const FileCell = ({ files = [], onUpload, onRemove, disabled, uploading }) => {
  const inputRef = useRef(null);
  return (
    <div style={{ minWidth: '120px' }}>
      {files.map((f) => (
        <div key={f.filename} style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px', fontSize: 'var(--fs-sm)' }}>
          <a
            href={`${API_ORIGIN}/uploads/${f.filename}`}
            target="_blank"
            rel="noopener noreferrer"
            title={f.originalname}
            style={{ color: 'var(--primary-color)', textDecoration: 'none', maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}
          >
            <FaEye style={{ marginRight: '3px', fontSize: 'var(--fs-xs)' }} />
            {f.originalname || f.filename}
          </a>
          {!disabled && (
            <button
              type="button"
              onClick={() => onRemove(f.filename)}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger-color)', padding: '0 2px', lineHeight: 1, fontSize: 'var(--fs-xs)' }}
              title="Remove file"
            >
              <FaTimes />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <>
          <input ref={inputRef} type="file" style={{ display: 'none' }} onChange={(e) => { if (e.target.files[0]) onUpload(e.target.files[0]); e.target.value = ''; }} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            style={{ fontSize: 'var(--fs-xs)', padding: '2px 7px', borderRadius: '4px', border: '1px dashed var(--border-color)', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap' }}
          >
            <FaPaperclip style={{ fontSize: 'var(--fs-2xs)' }} />
            {uploading ? 'Uploading…' : 'Attach'}
          </button>
        </>
      )}
    </div>
  );
};

const AnalyticalMethods = () => {
  const [loading, setLoading] = useState(true);
  const [surfaces, setSurfaces] = useState([]);     // unique materials from equipment
  const [record, setRecord] = useState(null);       // saved DB record
  const [rowData, setRowData] = useState([]);        // editable rows
  const [isDirty, setIsDirty] = useState(false);
  const [methodMeta, setMethodMeta] = useState({ method_version: '', specificity_demonstrated: false, specificity_notes: '', linearity_range: '', precision_rsd_percent: '' });
  const [uploadingFor, setUploadingFor] = useState(null); // {idx, fileType}

  // Optional train scoping, purely to surface the same live surface area MACO
  // already computes — does not gate saving/approving an AMV record.
  const [trains, setTrains] = useState([]);
  const [selectedTrainId, setSelectedTrainId] = useState('');
  const [trainArea, setTrainArea] = useState(null);
  const [trainAreaLoading, setTrainAreaLoading] = useState(false);
  // Whether any equipment on this line is actually detergent-cleaned (Cleaning
  // Config) — gates whether the Detergent Residue recovery columns show at all.
  const [detergentActive, setDetergentActive] = useState(false);

  // E-sign modal
  const [showESign, setShowESign] = useState(false);
  const [eSignMode, setESignMode] = useState('draft'); // 'draft' | 'submit' | 'product' | 'qa'
  const [eSignData, setESignData] = useState({ password: '', comment: '', decision: 'approve' });
  const [eSigning, setESigning] = useState(false);
  const passwordRef = useRef(null);

  const activePlant = localStorage.getItem('activePlant') || '';
  const activeLine  = localStorage.getItem('activeLine')  || '';
  const user        = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const { canEarlier: canProductApprove, canFinal: canQaApprove, earlierLabel, finalLabel } = useApprovalRights('analytical_recovery.approval');
  const status            = record?.status || 'Draft';
  const isApproved        = status === 'Approved';
  const canEdit           = !isApproved && status !== 'Pending Approval' && status !== 'Pending QA Approval';

  // ── Load data ─────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    const isNoLine = !activeLine || activeLine === 'All' || activeLine === 'All Lines';
    if (isNoLine) {
      setSurfaces([]); setRecord(null); setRowData([]); setIsDirty(false);
      setTrains([]); setSelectedTrainId(''); setTrainArea(null); setDetergentActive(false);
      setLoading(false);
      return;
    }
    try {
      const [surfRes, recRes, trainsRes, ccRes] = await Promise.all([
        analyticalMethodsAPI.getSurfaces({ plant_code: activePlant, production_line: activeLine }),
        analyticalMethodsAPI.getRecovery({ plant_code: activePlant, production_line: activeLine }),
        equipmentAPI.getTrains().catch(() => ({ data: { data: [] } })),
        cleaningConfigAPI.getAll({ line_id: activeLine }).catch(() => ({ data: { data: [] } })),
      ]);
      setDetergentActive((ccRes?.data?.data || []).some((r) => r.detergent_used && r.detergent_name));

      const surfaceList = surfRes?.data?.data || [];
      setSurfaces(surfaceList);

      const allTrains = trainsRes?.data?.data || [];
      setTrains(allTrains.filter((t) => !t.production_line || String(t.production_line).toLowerCase() === String(activeLine).toLowerCase()));

      const saved = recRes?.data?.data || null;
      setRecord(saved);
      setSelectedTrainId(saved?.equipment_train_id ? String(saved.equipment_train_id) : '');
      setMethodMeta({
        method_version: saved?.method_version || '',
        specificity_demonstrated: !!saved?.specificity_demonstrated,
        specificity_notes: saved?.specificity_notes || '',
        linearity_range: saved?.linearity_range || '',
        precision_rsd_percent: saved?.precision_rsd_percent ?? '',
      });

      const savedSurfaces = Array.isArray(saved?.surfaces) ? saved.surfaces : [];
      const savedMap = {};
      savedSurfaces.forEach(s => { savedMap[s.surface_material] = s; });

      const allSurfaces = new Set([
        ...surfaceList,
        ...savedSurfaces.map(s => s.surface_material)
      ]);

      const rows = Array.from(allSurfaces).map(mat => savedMap[mat] || emptyRow(mat));
      setRowData(rows);
      setIsDirty(false);
    } catch (err) {
      toast.error('Failed to load analytical methods data');
    } finally {
      setLoading(false);
    }
  }, [activePlant, activeLine]);

  useEffect(() => { loadData(); }, [loadData]);

  // Read-only live area display — never writable, so it can't drift from the
  // same calculation MACO uses.
  useEffect(() => {
    if (!selectedTrainId) { setTrainArea(null); return; }
    let cancelled = false;
    setTrainAreaLoading(true);
    analyticalMethodsAPI.getTrainArea(selectedTrainId)
      .then((res) => { if (!cancelled) setTrainArea(res?.data?.data || null); })
      .catch(() => { if (!cancelled) setTrainArea(null); })
      .finally(() => { if (!cancelled) setTrainAreaLoading(false); });
    return () => { cancelled = true; };
  }, [selectedTrainId]);

  useEffect(() => {
    if (showESign) setTimeout(() => passwordRef.current?.focus(), 80);
  }, [showESign]);

  // ── Row editing ────────────────────────────────────────────────────────────
  const updateRow = (idx, field, value) => {
    setRowData(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setIsDirty(true);
  };

  // ── File upload ────────────────────────────────────────────────────────────
  const handleUpload = async (idx, fileType, file) => {
    setUploadingFor({ idx, fileType });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await analyticalMethodsAPI.uploadFile(fd);
      const uploaded = res?.data?.data;
      if (!uploaded) throw new Error('Upload failed');
      setRowData(prev => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          [fileType]: [...(next[idx][fileType] || []), { filename: uploaded.filename, originalname: uploaded.originalname }]
        };
        return next;
      });
      setIsDirty(true);
    } catch {
      toast.error('File upload failed');
    } finally {
      setUploadingFor(null);
    }
  };

  const handleRemoveFile = (idx, fileType, filename) => {
    setRowData(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [fileType]: (next[idx][fileType] || []).filter(f => f.filename !== filename) };
      return next;
    });
    setIsDirty(true);
    analyticalMethodsAPI.deleteFile(filename).catch(() => {});
  };

  // ── E-sign modal ───────────────────────────────────────────────────────────
  const openESign = (mode) => {
    if (mode === 'draft' && record?.amr_id && !isDirty) {
      toast.info('No changes were made — nothing to save, so no signature is required.');
      return;
    }
    setESignMode(mode);
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESign(true);
  };

  const closeESign = () => { setShowESign(false); setESigning(false); };

  const handleConfirmESign = async () => {
    if (!eSignData.password) return toast.error('Password required');
    if (!eSignData.comment.trim()) return toast.error('Comment / reason required');
    setESigning(true);
    try {
      if (eSignMode === 'draft' || eSignMode === 'submit') {
        // Save data first
        const payload = {
          plant_code: activePlant,
          production_line: activeLine,
          equipment_train_id: selectedTrainId || null,
          surfaces: JSON.stringify(rowData),
          comment: eSignData.comment,
          password: eSignData.password,
          amr_id: record?.amr_id || undefined,
          ...methodMeta,
        };
        const saveRes = await analyticalMethodsAPI.saveRecovery(payload);
        const saved = saveRes?.data?.data;
        if (!saved) throw new Error('Save failed');
        setRecord(saved);
        setIsDirty(false);

        if (eSignMode === 'submit') {
          const subRes = await analyticalMethodsAPI.submitRecovery(saved.amr_id, {
            password: eSignData.password,
            comment: eSignData.comment,
          });
          setRecord(subRes?.data?.data || saved);
          toast.success('Submitted for approval');
        } else {
          toast.success('Saved as Draft');
        }
      } else if (eSignMode === 'product' || eSignMode === 'qa') {
        const res = await analyticalMethodsAPI.approveRecovery(record.amr_id, {
          stage: eSignMode,
          decision: eSignData.decision,
          comment: eSignData.comment,
          password: eSignData.password,
        });
        setRecord(res?.data?.data);
        const decLabel = eSignData.decision === 'approve' ? 'Approved' : eSignData.decision === 'return' ? 'Returned to Draft' : 'Rejected';
        toast.success(`${eSignMode === 'product' ? 'Product' : 'QA'} review: ${decLabel}`);
      }
      closeESign();
      await loadData();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Action failed');
    } finally {
      setESigning(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  const noLine = !activeLine || activeLine === 'All';

  const eSignTitle = {
    draft:   'Save as Draft — Electronic Signature',
    submit:  'Submit for Approval — Electronic Signature',
    product: `${earlierLabel} — Electronic Signature`,
    qa:      `${finalLabel} — Electronic Signature`,
  }[eSignMode];

  const eSignMsg = {
    draft:   'Saving requires your electronic signature.',
    submit:  'Submitting for approval requires your electronic signature.',
    product: 'Product approval action requires your electronic signature.',
    qa:      'QA approval action requires your electronic signature.',
  }[eSignMode];

  return (
    <div>
      {/* ── Page Header ────────────────────────────────────────────────────── */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <h2 className="page-title">Analytical Method — Recovery Study</h2>
              <p className="page-subtitle">Swab/rinse recovery, LOD/LOQ and validation documentation per equipment contact surface.</p>
            </div>
            <StatusBadge status={status} />
          </div>
          <div className="toolbar-full" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {canEdit && !noLine && (
              <>
                <button className="btn btn-outline btn-sm" onClick={() => openESign('draft')} disabled={!isDirty}>
                  <FaSave /> Save Draft
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => openESign('submit')} disabled={!record && !rowData.length}>
                  <FaCheck /> Submit for Approval
                </button>
              </>
            )}
            {status === 'Pending Approval' && canProductApprove && (
              <button className="btn btn-success btn-sm" onClick={() => openESign('product')}>
                <FaCheck /> {earlierLabel}
              </button>
            )}
            {status === 'Pending QA Approval' && canQaApprove && (
              <button className="btn btn-success btn-sm" onClick={() => openESign('qa')}>
                <FaCheck /> {finalLabel}
              </button>
            )}
          </div>
          <div className="toolbar-compact" style={{ gap: '8px', alignItems: 'center' }}>
            {canEdit && !noLine && (
              <button className="btn btn-primary btn-sm" onClick={() => openESign('submit')} disabled={!record && !rowData.length}>
                <FaCheck /> Submit for Approval
              </button>
            )}
            <ActionsMenu
              title="More actions"
              items={[
                { label: 'Save Draft', icon: <FaSave />, disabled: !isDirty, show: canEdit && !noLine, onClick: () => openESign('draft') },
                { label: earlierLabel, icon: <FaCheck />, show: status === 'Pending Approval' && canProductApprove, onClick: () => openESign('product') },
                { label: finalLabel, icon: <FaCheck />, show: status === 'Pending QA Approval' && canQaApprove, onClick: () => openESign('qa') },
              ]}
            />
          </div>
        </div>

        {/* ── Context bar ─────────────────────────────────────────────────── */}
        <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', fontSize: 'var(--fs-md)', background: 'var(--bg-color)' }}>
          <span><strong>Unit / Plant:</strong> {activePlant || 'All'}</span>
          <span><strong>Section:</strong> {activeLine || 'All'}</span>
          {noLine && (
            <span style={{ color: 'var(--warning-text)', background: 'var(--warning-bg)', border: '1px solid var(--warning-border)', borderRadius: '6px', padding: '2px 10px' }}>
              <FaInfoCircle style={{ marginRight: '4px' }} />
              Please select a specific Section to load equipment surfaces.
            </span>
          )}
        </div>

        {/* ── Approval trail ──────────────────────────────────────────────── */}
        {record && (
          <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
            {record.saved_by_name && (
              <span>
                <strong>Saved by:</strong> {record.saved_by_name}
                {record.saved_at && <> on {new Date(record.saved_at).toLocaleString()}</>}
              </span>
            )}
            {record.product_approved_by_name && (
              <span>
                <strong>Product review:</strong> {record.product_approval_decision} by {record.product_approved_by_name}
                {record.product_approved_at && <> on {new Date(record.product_approved_at).toLocaleString()}</>}
                {record.product_approval_comment && <> — "{record.product_approval_comment}"</>}
              </span>
            )}
            {record.qa_approved_by_name && (
              <span>
                <strong>QA review:</strong> {record.qa_approval_decision} by {record.qa_approved_by_name}
                {record.qa_approved_at && <> on {new Date(record.qa_approved_at).toLocaleString()}</>}
                {record.qa_approval_comment && <> — "{record.qa_approval_comment}"</>}
              </span>
            )}
          </div>
        )}

        {!noLine && (
          <div style={{ padding: '16px 20px 0' }}>
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '14px' }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 'var(--fs-base)', fontWeight: 700 }}>Method Validation</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
                <div>
                  <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Method Version</label>
                  <input className="form-control form-control-sm" value={methodMeta.method_version}
                    onChange={(e) => { setMethodMeta((p) => ({ ...p, method_version: e.target.value })); setIsDirty(true); }}
                    placeholder="e.g. v2.1" />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Linearity Range</label>
                  <input className="form-control form-control-sm" value={methodMeta.linearity_range}
                    onChange={(e) => { setMethodMeta((p) => ({ ...p, linearity_range: e.target.value })); setIsDirty(true); }}
                    placeholder="e.g. 0.5–150% of target" />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Precision (%RSD)</label>
                  <input className="form-control form-control-sm" type="number" step="any" min="0" value={methodMeta.precision_rsd_percent}
                    onChange={(e) => { setMethodMeta((p) => ({ ...p, precision_rsd_percent: e.target.value })); setIsDirty(true); }}
                    placeholder="e.g. 1.8" />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '22px' }}>
                  <input id="specificity-demonstrated" type="checkbox" checked={methodMeta.specificity_demonstrated}
                    onChange={(e) => { setMethodMeta((p) => ({ ...p, specificity_demonstrated: e.target.checked })); setIsDirty(true); }} />
                  <label htmlFor="specificity-demonstrated" className="form-label" style={{ margin: 0, fontSize: 'var(--fs-md)' }}>Specificity Demonstrated</label>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Specificity Notes</label>
                  <textarea className="form-control form-control-sm" rows={2} value={methodMeta.specificity_notes}
                    onChange={(e) => { setMethodMeta((p) => ({ ...p, specificity_notes: e.target.value })); setIsDirty(true); }}
                    placeholder="e.g. No interference from placebo/detergent at the reporting limit..." />
                </div>
              </div>
            </div>
          </div>
        )}

        {!noLine && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 20px 0', flexWrap: 'wrap' }}>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Equipment Train (optional)</label>
              <select className="form-control form-control-sm" style={{ minWidth: '220px' }} value={selectedTrainId}
                disabled={!canEdit}
                onChange={(e) => { setSelectedTrainId(e.target.value); setIsDirty(true); }}>
                <option value="">— Not linked to a train —</option>
                {trains.map((t) => (
                  <option key={t.train_id} value={t.train_id}>{t.train_name}</option>
                ))}
              </select>
            </div>
            {selectedTrainId && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }} title="Live surface area, same calculation MACO uses — read-only">
                <FaRulerCombined />
                {trainAreaLoading ? 'Loading area…' : trainArea
                  ? <span><strong>{Number(trainArea.effective_surface_area_cm2).toLocaleString(undefined, { maximumFractionDigits: 2 })} cm²</strong> effective surface area ({trainArea.area_compensation_percent}% compensation applied)</span>
                  : 'Area unavailable'}
              </div>
            )}
          </div>
        )}

        {!noLine && rowData.length > 0 && (() => {
          const filled = (v) => v !== undefined && v !== null && String(v).trim() !== '';
          const recoveryDone = rowData.filter((r) => filled(r.swab_chem_pct) && filled(r.rinse_chem_pct)).length;
          const limitsDone = rowData.filter((r) => filled(r.lod_value) && filled(r.loq_value)).length;
          return (
            <div className="stat-card-grid" style={{ padding: '12px 20px 0' }}>
              <StatCard compact title="Contact Surfaces" value={rowData.length} icon={FaLayerGroup} color="primary" />
              <StatCard compact title="Recovery Entered" value={recoveryDone} icon={FaFlask} color="success" />
              <StatCard compact title="Recovery Pending" value={rowData.length - recoveryDone} icon={FaExclamationTriangle} color="warning" />
              <StatCard compact title="LOD / LOQ Defined" value={limitsDone} icon={FaRulerCombined} color="info" />
            </div>
          );
        })()}

        {/* ── Table ───────────────────────────────────────────────────────── */}
        {noLine ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Select a Section from the header to load equipment contact surfaces.
          </div>
        ) : rowData.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            No equipment contact surfaces found for this section. Add surfaces via Equipment Master → Parts.
          </div>
        ) : (
          <FloatingScrollWrapper style={{ margin: '0' }}>
            <table style={{ minWidth: '1520px' }}>
              <thead>
                <tr>
                  <th style={{ minWidth: '36px', textAlign: 'center' }}>#</th>
                  <th style={{ minWidth: '160px' }}>Surface Material</th>
                  {/* Chemical (API Residue) */}
                  <th colSpan={2} style={{ textAlign: 'center', background: 'color-mix(in srgb, #2563eb 8%, var(--bg-color))', borderBottom: 'none' }}>
                    Chemical / API Residue
                  </th>
                  {/* Detergent Residue — only when this line actually uses a detergent */}
                  {detergentActive && (
                    <th colSpan={2} style={{ textAlign: 'center', background: 'color-mix(in srgb, #b45309 8%, var(--bg-color))', borderBottom: 'none' }}>
                      Detergent Residue
                    </th>
                  )}
                  {/* Microbial */}
                  <th colSpan={2} style={{ textAlign: 'center', background: 'color-mix(in srgb, #10b981 8%, var(--bg-color))', borderBottom: 'none' }}>
                    Microbial
                  </th>
                  {/* LOD / LOQ */}
                  <th colSpan={2} style={{ textAlign: 'center', background: 'color-mix(in srgb, #f59e0b 8%, var(--bg-color))', borderBottom: 'none' }}>
                    API Residue — LOD / LOQ
                  </th>
                  <th style={{ minWidth: '130px' }}>Validation Protocol</th>
                  <th style={{ minWidth: '130px' }}>Validation Report</th>
                  <th style={{ minWidth: '130px' }}>Study Reports</th>
                  <th style={{ minWidth: '180px' }}>Remarks</th>
                </tr>
                <tr>
                  <th />
                  <th />
                  <th style={{ minWidth: '110px', background: 'color-mix(in srgb, #2563eb 5%, var(--bg-color))' }}>Swab Recovery %</th>
                  <th style={{ minWidth: '110px', background: 'color-mix(in srgb, #2563eb 5%, var(--bg-color))' }}>Rinse Recovery %</th>
                  {detergentActive && (
                    <>
                      <th style={{ minWidth: '110px', background: 'color-mix(in srgb, #b45309 5%, var(--bg-color))' }}>Swab Recovery %</th>
                      <th style={{ minWidth: '110px', background: 'color-mix(in srgb, #b45309 5%, var(--bg-color))' }}>Rinse Recovery %</th>
                    </>
                  )}
                  <th style={{ minWidth: '110px', background: 'color-mix(in srgb, #10b981 5%, var(--bg-color))' }}>Swab Recovery %</th>
                  <th style={{ minWidth: '110px', background: 'color-mix(in srgb, #10b981 5%, var(--bg-color))' }}>Rinse Recovery %</th>
                  <th style={{ minWidth: '155px', background: 'color-mix(in srgb, #f59e0b 5%, var(--bg-color))' }}>LOD</th>
                  <th style={{ minWidth: '155px', background: 'color-mix(in srgb, #f59e0b 5%, var(--bg-color))' }}>
                    <span>LOQ</span>
                    <span title="LOQ must be less than the lowest swab and rinse acceptance limit" style={{ marginLeft: '5px', color: 'var(--warning-color)', fontSize: 'var(--fs-xs)', cursor: 'help' }}>⚠ &lt; acceptance limit</span>
                  </th>
                  <th /><th /><th /><th />
                </tr>
              </thead>
              <tbody>
                {rowData.map((row, idx) => {
                  const isUploading = (t) => uploadingFor?.idx === idx && uploadingFor?.fileType === t;
                  return (
                    <tr key={row.surface_material || idx}>
                      <td style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>{idx + 1}</td>
                      <td>
                        <strong style={{ fontSize: 'var(--fs-base)' }}>{row.surface_material}</strong>
                        {!surfaces.includes(row.surface_material) && (
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--warning-color)', marginTop: '2px' }}>Saved — not in active equipment</div>
                        )}
                      </td>
                      {/* Swab Chemical % */}
                      <td style={{ background: 'color-mix(in srgb, #2563eb 3%, var(--card-bg))' }}>
                        <input
                          type="number" min="0" max="100" step="0.1"
                          className="form-control"
                          style={{ width: '90px', padding: '4px 8px', fontSize: 'var(--fs-base)' }}
                          value={row.swab_chem_pct}
                          disabled={!canEdit}
                          onChange={e => updateRow(idx, 'swab_chem_pct', e.target.value)}
                          placeholder="e.g. 85"
                        />
                      </td>
                      {/* Rinse Chemical % */}
                      <td style={{ background: 'color-mix(in srgb, #2563eb 3%, var(--card-bg))' }}>
                        <input
                          type="number" min="0" max="100" step="0.1"
                          className="form-control"
                          style={{ width: '90px', padding: '4px 8px', fontSize: 'var(--fs-base)' }}
                          value={row.rinse_chem_pct}
                          disabled={!canEdit}
                          onChange={e => updateRow(idx, 'rinse_chem_pct', e.target.value)}
                          placeholder="e.g. 92"
                        />
                      </td>
                      {detergentActive && (
                        <>
                          {/* Swab Detergent % */}
                          <td style={{ background: 'color-mix(in srgb, #b45309 3%, var(--card-bg))' }}>
                            <input
                              type="number" min="0" max="100" step="0.1"
                              className="form-control"
                              style={{ width: '90px', padding: '4px 8px', fontSize: 'var(--fs-base)' }}
                              value={row.swab_detergent_pct}
                              disabled={!canEdit}
                              onChange={e => updateRow(idx, 'swab_detergent_pct', e.target.value)}
                              placeholder="e.g. 85"
                            />
                          </td>
                          {/* Rinse Detergent % */}
                          <td style={{ background: 'color-mix(in srgb, #b45309 3%, var(--card-bg))' }}>
                            <input
                              type="number" min="0" max="100" step="0.1"
                              className="form-control"
                              style={{ width: '90px', padding: '4px 8px', fontSize: 'var(--fs-base)' }}
                              value={row.rinse_detergent_pct}
                              disabled={!canEdit}
                              onChange={e => updateRow(idx, 'rinse_detergent_pct', e.target.value)}
                              placeholder="e.g. 92"
                            />
                          </td>
                        </>
                      )}
                      {/* Swab Micro % */}
                      <td style={{ background: 'color-mix(in srgb, #10b981 3%, var(--card-bg))' }}>
                        <input
                          type="number" min="0" max="100" step="0.1"
                          className="form-control"
                          style={{ width: '90px', padding: '4px 8px', fontSize: 'var(--fs-base)' }}
                          value={row.swab_micro_pct}
                          disabled={!canEdit}
                          onChange={e => updateRow(idx, 'swab_micro_pct', e.target.value)}
                          placeholder="e.g. 78"
                        />
                      </td>
                      {/* Rinse Micro % */}
                      <td style={{ background: 'color-mix(in srgb, #10b981 3%, var(--card-bg))' }}>
                        <input
                          type="number" min="0" max="100" step="0.1"
                          className="form-control"
                          style={{ width: '90px', padding: '4px 8px', fontSize: 'var(--fs-base)' }}
                          value={row.rinse_micro_pct}
                          disabled={!canEdit}
                          onChange={e => updateRow(idx, 'rinse_micro_pct', e.target.value)}
                          placeholder="e.g. 88"
                        />
                      </td>
                      {/* LOD */}
                      <td style={{ background: 'color-mix(in srgb, #f59e0b 3%, var(--card-bg))' }}>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <input
                            type="number" min="0" step="0.0001"
                            className="form-control"
                            style={{ width: '70px', padding: '4px 6px', fontSize: 'var(--fs-md)' }}
                            value={row.lod_value}
                            disabled={!canEdit}
                            onChange={e => updateRow(idx, 'lod_value', e.target.value)}
                            placeholder="0.0000"
                          />
                          <select
                            className="form-control"
                            style={{ width: '78px', padding: '4px 3px', fontSize: 'var(--fs-sm)' }}
                            value={row.lod_unit || 'µg/mL'}
                            disabled={!canEdit}
                            onChange={e => updateRow(idx, 'lod_unit', e.target.value)}
                          >
                            {CONC_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                      </td>
                      {/* LOQ */}
                      <td style={{ background: 'color-mix(in srgb, #f59e0b 3%, var(--card-bg))' }}>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <input
                            type="number" min="0" step="0.0001"
                            className="form-control"
                            style={{ width: '70px', padding: '4px 6px', fontSize: 'var(--fs-md)' }}
                            value={row.loq_value}
                            disabled={!canEdit}
                            onChange={e => updateRow(idx, 'loq_value', e.target.value)}
                            placeholder="0.0000"
                          />
                          <select
                            className="form-control"
                            style={{ width: '78px', padding: '4px 3px', fontSize: 'var(--fs-sm)' }}
                            value={row.loq_unit || 'µg/mL'}
                            disabled={!canEdit}
                            onChange={e => updateRow(idx, 'loq_unit', e.target.value)}
                          >
                            {CONC_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                        {row.loq_value && (
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--warning-text)', marginTop: '4px' }}>
                            Verify &lt; swab &amp; rinse acceptance limits
                          </div>
                        )}
                      </td>
                      {/* Validation Protocol */}
                      <td>
                        <FileCell
                          files={row.protocol_files}
                          disabled={!canEdit}
                          uploading={isUploading('protocol_files')}
                          onUpload={(f) => handleUpload(idx, 'protocol_files', f)}
                          onRemove={(fn) => handleRemoveFile(idx, 'protocol_files', fn)}
                        />
                      </td>
                      {/* Validation Report */}
                      <td>
                        <FileCell
                          files={row.report_files}
                          disabled={!canEdit}
                          uploading={isUploading('report_files')}
                          onUpload={(f) => handleUpload(idx, 'report_files', f)}
                          onRemove={(fn) => handleRemoveFile(idx, 'report_files', fn)}
                        />
                      </td>
                      {/* Study Reports */}
                      <td>
                        <FileCell
                          files={row.study_files}
                          disabled={!canEdit}
                          uploading={isUploading('study_files')}
                          onUpload={(f) => handleUpload(idx, 'study_files', f)}
                          onRemove={(fn) => handleRemoveFile(idx, 'study_files', fn)}
                        />
                      </td>
                      {/* Remarks */}
                      <td>
                        <textarea
                          className="form-control"
                          rows={2}
                          style={{ width: '100%', minWidth: '160px', fontSize: 'var(--fs-md)', resize: 'vertical', padding: '4px 8px' }}
                          value={row.remarks}
                          disabled={!canEdit}
                          onChange={e => updateRow(idx, 'remarks', e.target.value)}
                          placeholder="Remarks / notes…"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </FloatingScrollWrapper>
        )}

        {/* ── Bottom action bar ───────────────────────────────────────────── */}
        {!noLine && rowData.length > 0 && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>
              {rowData.length} surface{rowData.length !== 1 ? 's' : ''} listed
              {isDirty && <span style={{ color: 'var(--warning-color)', marginLeft: '8px' }}>● Unsaved changes</span>}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {canEdit && (
                <>
                  <button className="btn btn-outline btn-sm" onClick={() => openESign('draft')} disabled={!isDirty}>
                    <FaSave /> Save as Draft
                  </button>
                  {record && (
                    <button className="btn btn-primary btn-sm" onClick={() => openESign('submit')}>
                      <FaFileUpload /> Submit for Approval
                    </button>
                  )}
                </>
              )}
              {status === 'Pending Approval' && canProductApprove && (
                <button className="btn btn-success btn-sm" onClick={() => openESign('product')}>
                  <FaCheck /> {earlierLabel}
                </button>
              )}
              {status === 'Pending QA Approval' && canQaApprove && (
                <button className="btn btn-success btn-sm" onClick={() => openESign('qa')}>
                  <FaCheck /> {finalLabel}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── E-Sign Modal ──────────────────────────────────────────────────── */}
      {showESign && (
        <div
          className="modal-overlay"
          style={{ backdropFilter: 'blur(3px)', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeESign(); }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '95%' }}>
            <div className="modal-header">
              <h2 className="modal-title" style={{ fontSize: 'var(--fs-xl)' }}>{eSignTitle}</h2>
              <button className="modal-close" onClick={closeESign}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-base)', marginBottom: '16px' }}>{eSignMsg}</p>

              {(eSignMode === 'product' || eSignMode === 'qa') && (
                <div className="form-group">
                  <label className="form-label">Decision *</label>
                  <select className="form-control" value={eSignData.decision} onChange={e => setESignData(p => ({ ...p, decision: e.target.value }))}>
                    {APPROVAL_DECISIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Login Password *</label>
                <input
                  ref={passwordRef}
                  type="password"
                  className="form-control"
                  autoComplete="new-password"
                  data-lpignore="true"
                  value={eSignData.password}
                  onChange={e => setESignData(p => ({ ...p, password: e.target.value }))}
                  placeholder="Enter your login password"
                  onKeyDown={e => { if (e.key === 'Enter') handleConfirmESign(); }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Reason / Comment *</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={eSignData.comment}
                  onChange={e => setESignData(p => ({ ...p, comment: e.target.value }))}
                  placeholder="Provide reason or comment…"
                />
              </div>

              {/* Signer details preview */}
              <div style={{ padding: '8px 12px', background: 'var(--bg-color)', borderRadius: '6px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>
                <strong>Signed by:</strong> {user.username || 'Unknown'} &nbsp;|&nbsp;
                <strong>Date / Time:</strong> {new Date().toLocaleString()} &nbsp;|&nbsp;
                <strong>Role:</strong> {user.role}
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
              <button className="btn btn-outline" onClick={closeESign} disabled={eSigning}>Cancel</button>
              <button className="btn btn-primary" onClick={handleConfirmESign} disabled={eSigning}>
                {eSigning ? 'Processing…' : (
                  eSignMode === 'draft'   ? 'Confirm & Save Draft' :
                  eSignMode === 'submit'  ? 'Confirm & Submit' :
                  eSignMode === 'product' ? 'Confirm Product Decision' :
                                           'Confirm QA Decision'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticalMethods;
